/**
 * api/endpoints.ts
 * ──────────────────────────────────────────────────────────────────────────
 * SINGLE SOURCE OF TRUTH for every backend URL used in SK Hospitality.
 * Mirrors the SK Catalog pattern (src/api/endpoints.ts).
 *
 * Two backend services:
 *   PLATFORM_API → skplatform-api          (auth & identity — /v1/me, refresh,
 *                  logout, magic-link; issues the httpOnly cookies directly)
 *   HOSP_API     → sk-hospitality-adapter  (locations/sections/tables/settings,
 *                  reservations, orders, tickets, guest QR, dashboard SSE)
 *
 * Rules:
 *   - Zero hardcoded URLs anywhere else in the codebase.
 *   - To switch environments, update ONLY the base URLs below (injected at build
 *     time via webpack DefinePlugin).
 * ──────────────────────────────────────────────────────────────────────────
 */

// ── Base URLs (injected at build time — see webpack.config.js DefinePlugin) ──
export const PLATFORM_API: string = process.env.SKPLATFORM_API_URL || 'http://localhost:3013';
export const HOSP_API: string = process.env.VITE_HOSPITALITY_API_URL || 'http://localhost:3010';

// ── Versioned prefixes ──
const P = `${PLATFORM_API}/v1`; // skplatform-api v1 (auth/identity)
const H = `${HOSP_API}/v1`;     // hospitality adapter v1 (authenticated)

/* ═══════════════════════════════════════════════════════════════════════════
   PEOPLE (SK People) — via the adapter, which proxies to backbone's People
   gateway. Staff/server picker for sections. This app has no public route to
   backbone directly (no ingress, no allow-listed CORS origin) — every
   cross-product call goes frontend → adapter → backbone, same as guests/CRM.
═══════════════════════════════════════════════════════════════════════════ */
export const Hosp_Staff_Url = `${H}/staff`; // GET (list) | POST (create)

/* ═══════════════════════════════════════════════════════════════════════════
   PLATFORM API — Auth & Identity (httpOnly cookie session)
═══════════════════════════════════════════════════════════════════════════ */
export const Platform_Me_Url = `${P}/me`;                          // GET — identity probe (200 = logged in, 401 = not)
export const Platform_Token_Url = `${P}/auth/token`;               // GET — returns a JWT (carries org_name, org id)
export const Platform_Refresh_Url = `${P}/auth/refresh`;           // POST — silent rotation (httpOnly cookie)
export const Platform_Logout_Url = `${P}/auth/logout`;             // POST — clears session cookies (204)
export const Platform_Magic_Verify_Url = `${P}/auth/magic-verify`; // POST { token } — one-click login; sets cookies

/* ═══════════════════════════════════════════════════════════════════════════
   HOSPITALITY ADAPTER — Config (the only entities it persists)
═══════════════════════════════════════════════════════════════════════════ */
export const Hosp_Locations_Url = `${H}/locations`;                        // GET | POST
export const Hosp_Location_Url = (id: string) => `${H}/locations/${id}`;   // GET | PATCH | DELETE
// Fetches the org's platform locations (SSOT, owned by skplatform-api) through
// the adapter → backbone gateway, AND references each into hosp_location —
// so a fresh org always has a real, already-persisted locationId to work with
// (the adapter mints none of its own). Goes through the adapter rather than
// calling skplatform-api directly so it shares the adapter's own auth/CORS
// path, same as the People staff picker.
export const Hosp_Locations_Platform_Url = `${H}/locations/platform`;      // GET
export const Hosp_Sections_Url = `${H}/sections`;                          // GET | POST
export const Hosp_Section_Url = (id: string) => `${H}/sections/${id}`;     // PATCH | DELETE
export const Hosp_Tables_Url = `${H}/tables`;                              // GET | POST
export const Hosp_Table_Url = (id: string) => `${H}/tables/${id}`;         // PATCH | DELETE
// Frees a table with no linked queue entry (direct walk-ins never create
// one — see sk-hospitality-adapter-api settings.service.freeTableWithoutEntry).
export const Hosp_Table_Free_Url = (id: string) => `${H}/tables/${id}/free`; // POST
export const Hosp_Settings_Url = `${H}/settings`;                          // GET | PUT
export const Hosp_Setting_Url = (id: string) => `${H}/settings/${id}`;     // DELETE

/* ═══════════════════════════════════════════════════════════════════════════
   HOSPITALITY ADAPTER — Reservations / Walk-ins (delegates to SK Calendar)
═══════════════════════════════════════════════════════════════════════════ */
// Guest identity (sk-CRM contacts via the adapter), deduped by phone.
export const Hosp_Guests_Url = `${H}/guests`;                                   // GET ?q=
export const Hosp_Guests_FindOrCreate_Url = `${H}/guests/find-or-create`;        // POST
export const Hosp_Guests_Batch_Url = `${H}/guests/batch`;                        // GET ?ids=

export const Hosp_Reservations_Url = `${H}/reservations`;                       // GET | POST
export const Hosp_Reservation_Availability_Url = `${H}/reservations/availability`; // GET
export const Hosp_Reservation_Tables_Url = `${H}/reservations/tables`;             // GET — per-table free/occupied at a slot
export const Hosp_Reservation_Url = (id: string) => `${H}/reservations/${id}`;  // PATCH | DELETE
export const Hosp_Walkins_Url = `${H}/walkins`;                                 // POST
export const Hosp_Waitlist_Url = `${H}/waitlist`;                               // GET ?locationId=
export const Hosp_Waitlist_Seat_Url = (id: string) => `${H}/waitlist/${id}/seat`; // POST
export const Hosp_Waitlist_Release_Url = (id: string) => `${H}/waitlist/${id}/release`; // POST
export const Hosp_Waitlist_Remove_Url = (id: string) => `${H}/waitlist/${id}/remove`; // POST — remove a still-WAITING entry (release/done is ALLOCATED/SERVING only)

/* ═══════════════════════════════════════════════════════════════════════════
   HOSPITALITY ADAPTER — Orders + upsell + billing (SK Catalog / Upsell / Billing)
═══════════════════════════════════════════════════════════════════════════ */
export const Hosp_Menu_Url = `${H}/menu`;                                            // GET ?q=

/* ── Menu authoring (Settings → Menu): Dishes / Sections / Menus / Labels / Combos ──
   All map to SK Catalog via the adapter's /menu-admin surface (tools-first). */
export const Hosp_MenuAdmin_Provision_Url = `${H}/menu-admin/provision`;                       // POST
export const Hosp_MenuAdmin_Items_Url = `${H}/menu-admin/items`;                               // POST
export const Hosp_MenuAdmin_Item_Url = (id: string) => `${H}/menu-admin/items/${id}`;          // PATCH
export const Hosp_MenuAdmin_Item_Publish_Url = (id: string) => `${H}/menu-admin/items/${id}/publish`; // POST
export const Hosp_MenuAdmin_Sections_Url = `${H}/menu-admin/sections`;                         // GET | POST
export const Hosp_MenuAdmin_Section_Url = (id: string) => `${H}/menu-admin/sections/${id}`;    // PATCH | DELETE
export const Hosp_MenuAdmin_Item_Sections_Url = (id: string) => `${H}/menu-admin/items/${id}/sections`; // PUT
export const Hosp_MenuAdmin_Menus_Url = `${H}/menu-admin/menus`;                               // GET | POST
export const Hosp_MenuAdmin_Menu_Url = (id: string) => `${H}/menu-admin/menus/${id}`;          // GET | PATCH | DELETE
export const Hosp_MenuAdmin_Menu_Items_Url = (id: string) => `${H}/menu-admin/menus/${id}/items`;       // POST
export const Hosp_MenuAdmin_Menu_Item_Url = (id: string, itemId: string) => `${H}/menu-admin/menus/${id}/items/${itemId}`; // DELETE
export const Hosp_MenuAdmin_Menu_Order_Url = (id: string) => `${H}/menu-admin/menus/${id}/items/order`; // PUT
export const Hosp_MenuAdmin_Labels_Url = `${H}/menu-admin/labels`;                             // GET | POST
export const Hosp_MenuAdmin_Label_Url = (id: string) => `${H}/menu-admin/labels/${id}`;        // DELETE
export const Hosp_MenuAdmin_Item_Labels_Url = (id: string) => `${H}/menu-admin/items/${id}/labels`;     // PUT
export const Hosp_MenuAdmin_Item_Combo_Url = (id: string) => `${H}/menu-admin/items/${id}/combo-components`; // GET | POST
export const Hosp_MenuAdmin_Item_Combo_Child_Url = (id: string, childId: string) => `${H}/menu-admin/items/${id}/combo-components/${childId}`; // DELETE
export const Hosp_Orders_Url = `${H}/orders`;                                       // POST
export const Hosp_Orders_Today_Url = `${H}/orders/today`;                           // GET
export const Hosp_Order_Upsell_Url = (id: string) => `${H}/orders/${id}/upsell`;    // POST
export const Hosp_Order_Items_Url = (id: string) => `${H}/orders/${id}/items`;      // POST | DELETE | PATCH (delete/patch target a line by description in the body)
export const Hosp_Order_Decline_Url = (id: string) => `${H}/orders/${id}/decline`;  // POST
export const Hosp_Order_Bill_Url = (id: string) => `${H}/orders/${id}/bill`;        // POST
export const Hosp_Order_Issue_Url = (id: string) => `${H}/orders/${id}/issue`;      // POST
export const Hosp_Order_Settle_Url = (id: string) => `${H}/orders/${id}/settle`;    // POST

/* ═══════════════════════════════════════════════════════════════════════════
   HOSPITALITY ADAPTER — Support (delegates to SK Tickets / Chat)
═══════════════════════════════════════════════════════════════════════════ */
export const Hosp_Tickets_Url = `${H}/tickets`;                                       // GET | POST
export const Hosp_Ticket_Url = (id: string) => `${H}/tickets/${id}`;                  // GET (detail + thread)
export const Hosp_Ticket_Handoff_Url = (id: string) => `${H}/tickets/${id}/handoff`;  // POST

/* ═══════════════════════════════════════════════════════════════════════════
   HOSPITALITY ADAPTER — Guest QR self-join (no JWT; tenant/location from QR)
═══════════════════════════════════════════════════════════════════════════ */
export const Hosp_Guest_Otp_Request_Url = `${HOSP_API}/guest/otp/request`;              // POST
export const Hosp_Guest_Otp_Verify_Url = `${HOSP_API}/guest/otp/verify`;                // POST
export const Hosp_Guest_Session_Url = (token: string) => `${HOSP_API}/guest/session/${token}`; // GET

/* ═══════════════════════════════════════════════════════════════════════════
   HOSPITALITY ADAPTER — Dashboard live counters (SSE)
═══════════════════════════════════════════════════════════════════════════ */
export const Hosp_Dashboard_Stream_Url = `${H}/dashboard/stream`; // EventSource (cookie auth)
export const Hosp_Floor_Stream_Url = `${H}/floor/stream`; // EventSource (cookie auth)
